import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { Button, TextField } from '@orijinworks/frontend-commons';
import { getPersonDisplayName } from '../../persons-manager.helpers';
import { useServiceModalHook } from './useServiceModalHook';
import { useServiceModalStyles } from './service-modal.style';

const personPropType = PropTypes.shape({
  displayFirstName: PropTypes.string,
  displayLastName: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  personId: PropTypes.number.isRequired,
  username: PropTypes.string,
});

const renderActiveCell = (editingId, editForm, patchEditForm, row) => {
  if (editingId === row.id) {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(editForm.active)}
            onChange={(event) => patchEditForm({ active: event.target.checked })}
            color="primary"
          />
        }
        label={editForm.active ? 'Active' : 'Inactive'}
      />
    );
  }

  return row.active ? 'Active' : 'Inactive';
};

const renderRowActions = ({ row, editingId, saving, saveEdit, cancelEdit, startEditRow, setDeleteId, classes }) => {
  if (editingId === row.id) {
    return (
      <Box className={classes.editActions}>
        <Button size="small" color="primary" disabled={saving} onClick={() => saveEdit(row)}>
          Save
        </Button>
        <Button size="small" disabled={saving} onClick={cancelEdit}>
          Cancel
        </Button>
      </Box>
    );
  }

  return (
    <>
      <IconButton size="small" aria-label="edit service assignment" onClick={() => startEditRow(row)}>
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" aria-label="delete service assignment" onClick={() => setDeleteId(row.id)}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </>
  );
};

export function ServiceModal({ open, person, onClose, onSaved }) {
  const classes = useServiceModalStyles();
  const {
    addForm,
    adding,
    cancelAdd,
    cancelEdit,
    deleteId,
    editForm,
    editingId,
    error,
    handleAdd,
    handleDelete,
    loading,
    patchAddForm,
    patchEditForm,
    rows,
    saveEdit,
    saving,
    serviceCatalog,
    setAdding,
    setDeleteId,
    startEditRow,
  } = useServiceModalHook({ onSaved, open, person });

  if (!person) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Person services
        <Typography variant="body2" className={classes.dialogSubtitle}>
          {getPersonDisplayName(person)} · ID {person.personId}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={32} />
          </Box>
        )}
        {!loading && error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
        {!loading && (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell>Ext Username</TableCell>
                  <TableCell>External ID</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.displayName || row.serviceName}</TableCell>
                    <TableCell>
                      {editingId === row.id ? (
                        <TextField
                          value={editForm.extUsername}
                          onChange={(event) => patchEditForm({ extUsername: event.target.value })}
                          variant="outlined"
                          size="small"
                          fullWidth
                        />
                      ) : (
                        row.extUsername || '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === row.id ? (
                        <TextField
                          value={editForm.externalId}
                          onChange={(event) => patchEditForm({ externalId: event.target.value })}
                          variant="outlined"
                          size="small"
                          fullWidth
                        />
                      ) : (
                        row.externalId || '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === row.id ? (
                        <TextField
                          value={editForm.endDate}
                          onChange={(event) => patchEditForm({ endDate: event.target.value })}
                          variant="outlined"
                          size="small"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      ) : (
                        row.endDate || '—'
                      )}
                    </TableCell>
                    <TableCell>{renderActiveCell(editingId, editForm, patchEditForm, row)}</TableCell>
                    <TableCell align="right">
                      {renderRowActions({
                        cancelEdit,
                        classes,
                        editingId,
                        row,
                        saveEdit,
                        saving,
                        setDeleteId,
                        startEditRow,
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {deleteId !== null && (
              <Box className={classes.deleteConfirmBox}>
                <Typography variant="body2">Delete this service assignment?</Typography>
                <Box className={classes.deleteConfirmActions}>
                  <Button size="small" color="primary" disabled={saving} onClick={handleDelete}>
                    Confirm delete
                  </Button>
                  <Button size="small" disabled={saving} onClick={() => setDeleteId(null)}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}

            {adding ? (
              <Box className={classes.addFormColumn}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="svc-key-label">Service</InputLabel>
                  <Select
                    labelId="svc-key-label"
                    value={addForm.serviceKey}
                    onChange={(event) => patchAddForm({ serviceKey: String(event.target.value) })}
                    label="Service"
                  >
                    {serviceCatalog.map((svc) => (
                      <MenuItem key={svc.serviceId} value={String(svc.serviceId)}>
                        {svc.displayName || svc.serviceName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Ext username (optional)"
                  value={addForm.extUsername}
                  onChange={(event) => patchAddForm({ extUsername: event.target.value })}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
                <TextField
                  label="External ID (optional)"
                  value={addForm.externalId}
                  onChange={(event) => patchAddForm({ externalId: event.target.value })}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Start date (optional)"
                  value={addForm.startDate}
                  onChange={(event) => patchAddForm({ startDate: event.target.value })}
                  variant="outlined"
                  size="small"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End date (optional)"
                  value={addForm.endDate}
                  onChange={(event) => patchAddForm({ endDate: event.target.value })}
                  variant="outlined"
                  size="small"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(addForm.active)}
                      onChange={(event) => patchAddForm({ active: event.target.checked })}
                      color="primary"
                    />
                  }
                  label={addForm.active ? 'Active' : 'Inactive'}
                />
                <Box>
                  <Button
                    size="small"
                    color="primary"
                    disabled={saving || addForm.serviceKey === ''}
                    onClick={handleAdd}
                  >
                    Save
                  </Button>
                  <Button size="small" disabled={saving} onClick={cancelAdd}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box mt={2}>
                <Button variant="outlined" size="small" color="primary" onClick={() => setAdding(true)}>
                  Add service
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ServiceModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  open: PropTypes.bool.isRequired,
  person: personPropType,
};

ServiceModal.defaultProps = {
  onSaved: undefined,
  person: null,
};
